/**
 * Utilidad para procesar grupos y subgrupos de formularios
 * Maneja la lógica recursiva de begin_group, end_group, begin_repeat, end_repeat
 */

/**
 * Procesa recursivamente los grupos anidados
 * @param {Array} preguntas - Array de preguntas a procesar
 * @param {number} indiceInicio - Índice donde comenzar el procesamiento
 * @param {number} nivel - Nivel de anidación actual
 * @returns {Object} - {grupos: Array, indiceProximo: number}
 */
const procesarGruposRecursivo = (preguntas, indiceInicio = 0, nivel = 0) => {
  const grupos = [];
  let i = indiceInicio;

  while (i < preguntas.length) {
    const pregunta = preguntas[i];

    if (!pregunta) {
      i++;
      continue;
    }

    switch (pregunta.type) {
      case 'begin_group': {
        // console.log(`${'  '.repeat(nivel)}📁 [GRUPOS] Nivel ${nivel}: Iniciando grupo "${pregunta.label || pregunta.name}"`);

        // Crear nuevo grupo
        const nuevoGrupo = {
          id: pregunta.name || `grupo_${i}_nivel_${nivel}`,
          nombre: pregunta.label || pregunta.name || `Grupo ${i + 1}`,
          label: pregunta.label,
          name: pregunta.name,
          tipo: 'grupo',
          nivel: nivel,
          preguntaOriginal: pregunta,
          relevant_transformed: pregunta.relevant_transformed, // Usar solo la expresión relevant_transformed (sin fallback)
          subgrupos: [], // Para begin_repeat
          gruposHijos: [], // Para grupos anidados (begin_group dentro de begin_group)
          preguntas: [],
          indiceOriginal: i,
        };

        // Procesar contenido del grupo recursivamente
        i++; // Avanzar después del begin_group
        const resultadoGrupo = procesarGruposRecursivo(preguntas, i, nivel + 1);

        // Separar grupos hijos de subgrupos y preguntas normales
        resultadoGrupo.grupos.forEach(item => {
          if (item.tipo === 'grupo') {
            nuevoGrupo.gruposHijos.push(item);
          } else if (item.tipo === 'subgrupo') {
            nuevoGrupo.subgrupos.push(item);
          } else {
            nuevoGrupo.preguntas.push(item);
          }
        });

        grupos.push(nuevoGrupo);
        i = resultadoGrupo.indiceProximo;

        // console.log(`${'  '.repeat(nivel)}✅ [GRUPOS] Nivel ${nivel}: Grupo "${nuevoGrupo.nombre}" completado (${nuevoGrupo.preguntas.length} preguntas, ${nuevoGrupo.subgrupos.length} subgrupos, ${nuevoGrupo.gruposHijos.length} grupos hijos)`);
        break;
      }

      case 'begin_repeat': {
        // console.log(`${'  '.repeat(nivel)}📂 [GRUPOS] Nivel ${nivel}: Iniciando subgrupo "${pregunta.label || pregunta.name}"`);

        // Crear nuevo subgrupo
        const nuevoSubgrupo = {
          id: pregunta.name || `subgrupo_${i}_nivel_${nivel}`,
          nombre: pregunta.label || pregunta.name || `Subgrupo ${i + 1}`,
          label: pregunta.label,
          name: pregunta.name,
          tipo: 'subgrupo',
          nivel: nivel,
          preguntaOriginal: pregunta,
          relevant_transformed: pregunta.relevant_transformed, // Usar solo la expresión relevant_transformed (sin fallback)
          preguntas: [],
          gruposHijos: [], // Los subgrupos también pueden tener grupos anidados
          indiceOriginal: i,
        };

        // Procesar contenido del subgrupo recursivamente
        i++; // Avanzar después del begin_repeat
        const resultadoSubgrupo = procesarGruposRecursivo(preguntas, i, nivel + 1);

        // Separar grupos hijos de preguntas normales
        resultadoSubgrupo.grupos.forEach(item => {
          if (item.tipo === 'grupo') {
            nuevoSubgrupo.gruposHijos.push(item);
          } else {
            nuevoSubgrupo.preguntas.push(item);
          }
        });

        grupos.push(nuevoSubgrupo);
        i = resultadoSubgrupo.indiceProximo;

        // console.log(`${'  '.repeat(nivel)}✅ [GRUPOS] Nivel ${nivel}: Subgrupo "${nuevoSubgrupo.nombre}" completado (${nuevoSubgrupo.preguntas.length} preguntas, ${nuevoSubgrupo.gruposHijos.length} grupos hijos)`);
        break;
      }

      case 'end_group':
      case 'end_repeat': {
        // console.log(`${'  '.repeat(nivel)}🔚 [GRUPOS] Nivel ${nivel}: Finalizando contenedor (${pregunta.type})`);
        // Retornar al nivel superior
        return {
          grupos: grupos,
          indiceProximo: i + 1,
        };
      }

      default: {
        // Pregunta normal - agregar al nivel actual
        // console.log(`${'  '.repeat(nivel)}❓ [GRUPOS] Nivel ${nivel}: Pregunta "${pregunta.label || pregunta.name}" (${pregunta.type})`);
        grupos.push({
          ...pregunta,
          indiceOriginal: i,
          nivel: nivel,
          contenedor: nivel === 0 ? 'raiz' : 'anidado',
          tipo: 'pregunta',
        });
        i++;
        break;
      }
    }
  }

  return {
    grupos: grupos,
    indiceProximo: i,
  };
};

/**
 * Calcula estadísticas recursivas de los grupos procesados
 * @param {Array} elementos - Array de elementos procesados
 * @returns {Object} - Estadísticas calculadas
 */
const calcularEstadisticasRecursivas = elementos => {
  let stats = {
    totalGrupos: 0,
    totalSubgrupos: 0,
    totalPreguntas: 0,
    nivelesMaximos: 0,
  };

  const procesarElemento = (elemento, nivelActual = 0) => {
    stats.nivelesMaximos = Math.max(stats.nivelesMaximos, nivelActual);

    if (elemento.tipo === 'grupo') {
      stats.totalGrupos++;
      // Contar las preguntas que pertenecen directamente a este grupo (no anidadas)
      if (elemento.preguntas) {
        stats.totalPreguntas += elemento.preguntas.length;
      }
      // Procesar grupos hijos
      if (elemento.gruposHijos) {
        elemento.gruposHijos.forEach(hijo => procesarElemento(hijo, nivelActual + 1));
      }
      // Procesar subgrupos
      if (elemento.subgrupos) {
        elemento.subgrupos.forEach(subgrupo => procesarElemento(subgrupo, nivelActual + 1));
      }
    } else if (elemento.tipo === 'subgrupo') {
      stats.totalSubgrupos++;
      // Contar las preguntas que pertenecen directamente a este subgrupo (no anidadas)
      if (elemento.preguntas) {
        stats.totalPreguntas += elemento.preguntas.length;
      }
      // Procesar grupos hijos del subgrupo
      if (elemento.gruposHijos) {
        elemento.gruposHijos.forEach(hijo => procesarElemento(hijo, nivelActual + 1));
      }
    } else if (elemento.tipo === 'pregunta') {
      stats.totalPreguntas++;
    }
  };

  elementos.forEach(elemento => procesarElemento(elemento, 0));
  return stats;
};

/**
 * Busca un grupo por ID de forma recursiva
 * @param {Array} elementos - Array de elementos donde buscar
 * @param {string} grupoId - ID del grupo a buscar
 * @returns {Object|null} - Grupo encontrado o null
 */
const obtenerGrupoPorId = (elementos, grupoId) => {
  const buscarRecursivo = elementos => {
    for (const elemento of elementos) {
      if (elemento.tipo === 'grupo' && elemento.id === grupoId) {
        return elemento;
      }

      if (elemento.subgrupos && elemento.subgrupos.length > 0) {
        const encontrado = buscarRecursivo(elemento.subgrupos);
        if (encontrado) return encontrado;
      }

      if (elemento.gruposHijos && elemento.gruposHijos.length > 0) {
        const encontrado = buscarRecursivo(elemento.gruposHijos);
        if (encontrado) return encontrado;
      }
    }
    return null;
  };

  return buscarRecursivo(elementos);
};

/**
 * Busca un subgrupo por ID de forma recursiva
 * @param {Array} elementos - Array de elementos donde buscar
 * @param {string} subgrupoId - ID del subgrupo a buscar
 * @returns {Object|null} - Subgrupo encontrado o null
 */
const obtenerSubgrupoPorId = (elementos, subgrupoId) => {
  const buscarRecursivo = elementos => {
    for (const elemento of elementos) {
      if (elemento.tipo === 'subgrupo' && elemento.id === subgrupoId) {
        return elemento;
      }

      if (elemento.subgrupos && elemento.subgrupos.length > 0) {
        const encontrado = buscarRecursivo(elemento.subgrupos);
        if (encontrado) return encontrado;
      }

      if (elemento.gruposHijos && elemento.gruposHijos.length > 0) {
        const encontrado = buscarRecursivo(elemento.gruposHijos);
        if (encontrado) return encontrado;
      }
    }
    return null;
  };

  return buscarRecursivo(elementos);
};

/**
 * Obtiene todas las preguntas de un grupo de forma recursiva
 * @param {Object} grupo - Grupo del cual obtener las preguntas
 * @returns {Array} - Array de preguntas
 */
const obtenerPreguntasDeGrupo = grupo => {
  const recopilarPreguntasRecursivo = elemento => {
    let preguntas = [];

    // Agregar preguntas directas del elemento
    if (elemento.preguntas && elemento.preguntas.length > 0) {
      preguntas = preguntas.concat(elemento.preguntas);
    }

    // Procesar subgrupos
    if (elemento.subgrupos && elemento.subgrupos.length > 0) {
      elemento.subgrupos.forEach(subgrupo => {
        preguntas = preguntas.concat(recopilarPreguntasRecursivo(subgrupo));
      });
    }

    // Procesar grupos hijos
    if (elemento.gruposHijos && elemento.gruposHijos.length > 0) {
      elemento.gruposHijos.forEach(grupoHijo => {
        preguntas = preguntas.concat(recopilarPreguntasRecursivo(grupoHijo));
      });
    }

    return preguntas;
  };

  return recopilarPreguntasRecursivo(grupo);
};

/**
 * Obtiene el árbol completo de un elemento (con todos sus hijos)
 * @param {Object} elemento - Elemento del cual obtener el árbol
 * @returns {Object} - Árbol completo del elemento
 */
const obtenerArbolCompleto = elemento => {
  return {
    ...elemento,
    totalPreguntas: obtenerPreguntasDeGrupo(elemento).length,
    totalSubgrupos: elemento.subgrupos ? elemento.subgrupos.length : 0,
    totalGruposHijos: elemento.gruposHijos ? elemento.gruposHijos.length : 0,
  };
};

/**
 * Procesa un array de preguntas y devuelve los grupos organizados
 * @param {Array} preguntas - Array de preguntas del formulario
 * @returns {Object} - {grupos: Array, estadisticas: Object}
 */
const procesarFormulario = preguntas => {
  if (!preguntas || preguntas.length === 0) {
    return {
      grupos: [],
      estadisticas: {
        totalGrupos: 0,
        totalSubgrupos: 0,
        totalPreguntas: 0,
        nivelesMaximos: 0,
      },
    };
  }

  // console.log('🏗️ [GRUPOS] Iniciando procesamiento recursivo de grupos y subgrupos...');

  // Iniciar procesamiento recursivo
  const resultado = procesarGruposRecursivo(preguntas, 0, 0);

  // Calcular estadísticas recursivas
  const estadisticas = calcularEstadisticasRecursivas(resultado.grupos);

  // console.log('🎉 [GRUPOS] Procesamiento recursivo completado:', {
  //   ...estadisticas,
  //   elementosRaiz: resultado.grupos.length
  // });

  return {
    grupos: resultado.grupos,
    estadisticas: estadisticas,
  };
};

/**
 * Obtiene el título de un grupo/subgrupo usando label o name como fallback
 * @param {Object} elemento - Elemento del cual obtener el título
 * @returns {string} - Título del elemento
 */
const obtenerTituloElemento = elemento => {
  return elemento.label || elemento.name || elemento.nombre || 'Sin título';
};

/**
 * Aplana la estructura recursiva para obtener todos los elementos en un array plano
 * @param {Array} elementos - Array de elementos a aplanar
 * @returns {Array} - Array plano con todos los elementos
 */
const aplanarEstructura = elementos => {
  const resultado = [];

  const procesarElemento = elemento => {
    resultado.push(elemento);

    // Procesar subgrupos
    if (elemento.subgrupos && elemento.subgrupos.length > 0) {
      elemento.subgrupos.forEach(procesarElemento);
    }

    // Procesar grupos hijos
    if (elemento.gruposHijos && elemento.gruposHijos.length > 0) {
      elemento.gruposHijos.forEach(procesarElemento);
    }
  };

  elementos.forEach(procesarElemento);
  return resultado;
};

export {
  procesarGruposRecursivo,
  calcularEstadisticasRecursivas,
  obtenerGrupoPorId,
  obtenerSubgrupoPorId,
  obtenerPreguntasDeGrupo,
  obtenerArbolCompleto,
  procesarFormulario,
  obtenerTituloElemento,
  aplanarEstructura,
};
